import { useState, useEffect, useCallback, useRef } from 'react';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../contexts/AuthContext';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from 'recharts';

const CHART_COLORS = {
  success: '#00e5a0',
  failed: '#ff4560',
  accent: '#00d4ff',
  muted: '#2a3f57',
};

function StatCard({ label, value, sub, color = 'var(--accent)', icon, trend }) {
  return (
    <div className="card animate-in" style={{ position: 'relative', overflow: 'hidden' }}>
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 2,
        background: color,
      }} />
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
            {label}
          </div>
          <div style={{ fontSize: 32, fontWeight: 800, color: 'var(--text-primary)', letterSpacing: '-0.03em', fontFamily: 'var(--font-mono)', lineHeight: 1 }}>
            {value ?? '—'}
          </div>
          {sub && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>{sub}</div>}
        </div>
        <div style={{
          fontSize: 22, color, opacity: 0.7,
          background: `${color}18`,
          borderRadius: 8, padding: '8px 10px',
        }}>{icon}</div>
      </div>
    </div>
  );
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: 'var(--bg-card)', border: '1px solid var(--border-bright)',
      borderRadius: 6, padding: '10px 14px', fontSize: 12,
    }}>
      <div style={{ color: 'var(--text-muted)', marginBottom: 6 }}>{label}</div>
      {payload.map(p => (
        <div key={p.name} style={{ color: p.color, fontFamily: 'var(--font-mono)' }}>
          {p.name}: {p.value}
        </div>
      ))}
    </div>
  );
}

// Inline skeleton placeholder for charts/tables while loading
function ChartSkeleton({ height = 220 }) {
  return (
    <div style={{ height, display: 'flex', flexDirection: 'column', gap: 8, justifyContent: 'flex-end', padding: '0 4px' }}>
      {[0.4, 0.7, 0.5, 0.9, 0.6, 0.8, 0.3, 0.75].map((h, i) => (
        <div key={i} className="skeleton" style={{ height: `${h * 100}%`, borderRadius: 3 }} />
      ))}
    </div>
  );
}

function TableSkeleton({ rows = 5, cols = 4 }) {
  return (
    <>
      {[...Array(rows)].map((_, i) => (
        <tr key={i}>
          {[...Array(cols)].map((_, j) => (
            <td key={j}><div className="skeleton" style={{ height: 12, width: `${60 + Math.random() * 30}%` }} /></td>
          ))}
        </tr>
      ))}
    </>
  );
}

export default function Dashboard() {
  const api = useApi();
  const { user } = useAuth();

  // Each data slice has its own loading state so sections render independently
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [tab, setTab] = useState('hourly');

  // Auto-refresh — default 5 minutes
  const defaultInterval = 300;
  const [refreshInterval, setRefreshInterval] = useState(() => {
    const stored = parseInt(localStorage.getItem('ss_refresh_interval'));
    // Only use stored value if it's one of our valid options
    return [300, 600, 900].includes(stored) ? stored : defaultInterval;
  });
  const [countdown, setCountdown] = useState(refreshInterval);
  const countdownRef = useRef(null);
  const refreshRef = useRef(null);

  const [historicalMaLoading, setHistoricalMaLoading] = useState(false);
  const [historicalMaSplit, setHistoricalMaSplit] = useState(null);
  const [datasource, setDatasource] = useState(null);
  const [dsToggling, setDsToggling] = useState(false);

  // Fetch current datasource on mount
  useEffect(() => {
    api.get('/dashboard/datasource').then(d => setDatasource(d.datasource)).catch(() => {});
  }, [api]);

  const handleDsToggle = async () => {
    setDsToggling(true);
    try {
      const d = await api.post('/dashboard/datasource');
      setDatasource(d.datasource);
      // Reload all data with new source
      loadAll();
      setHistoricalMaSplit(null);
    } catch (e) { console.error(e); }
    finally { setDsToggling(false); }
  };

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    try { setSummary(await api.get('/dashboard/summary')); }
    catch (e) { console.error(e); }
    finally { setSummaryLoading(false); }
  }, [api]);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try { setHistory(await api.get('/dashboard/stats-history')); }
    catch (e) { console.error(e); }
    finally { setHistoryLoading(false); }
  }, [api]);

  const loadHistoricalMa = useCallback(async () => {
    setHistoricalMaLoading(true);
    try {
      // Last 30 days to match the history chart
      const todate = new Date().toISOString().slice(0, 10);
      const fromdate = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
      const rows = await api.get(`/dashboard/medical-aid-split?fromdate=${fromdate}&todate=${todate}`);
      setHistoricalMaSplit(rows);
    } catch (e) { console.error(e); }
    finally { setHistoricalMaLoading(false); }
  }, [api]);

  const loadAll = useCallback(() => {
    loadSummary();
    loadHistory();
    setCountdown(refreshInterval);
  }, [loadSummary, loadHistory, refreshInterval]);

  // Initial load
  useEffect(() => { loadAll(); }, [loadAll]);

  // When switching to history tab, load the 30-day medical aid split if not yet loaded
  useEffect(() => {
    if (tab === 'history' && !historicalMaSplit) loadHistoricalMa();
  }, [tab, historicalMaSplit, loadHistoricalMa]);

  // Countdown ticker — runs every second
  useEffect(() => {
    clearInterval(countdownRef.current);
    setCountdown(refreshInterval);
    countdownRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) return refreshInterval;
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(countdownRef.current);
  }, [refreshInterval]);

  // Auto-refresh trigger — fires when countdown hits 0
  useEffect(() => {
    clearInterval(refreshRef.current);
    refreshRef.current = setInterval(() => {
      loadSummary();
      if (tab === 'history') { loadHistory(); loadHistoricalMa(); }
      setCountdown(refreshInterval);
    }, refreshInterval * 1000);
    return () => clearInterval(refreshRef.current);
  }, [refreshInterval, loadSummary, loadHistory, loadHistoricalMa, tab]);

  const handleIntervalChange = (val) => {
    const secs = parseInt(val);
    setRefreshInterval(secs);
    localStorage.setItem('ss_refresh_interval', secs);
    setCountdown(secs);
  };

  const handleManualRefresh = () => {
    loadAll();
    if (tab === 'history') loadHistoricalMa();
    clearInterval(refreshRef.current);
    refreshRef.current = setInterval(() => {
      loadSummary();
      if (tab === 'history') { loadHistory(); loadHistoricalMa(); }
      setCountdown(refreshInterval);
    }, refreshInterval * 1000);
  };

  // Format countdown as mm:ss
  const formatCountdown = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return m > 0 ? `${m}:${String(s).padStart(2, '0')}` : `${s}s`;
  };

  const { recentTransactions, hourlyVolume, topMailboxes, medicalAidSplit: dailyMaSplit } = summary || {};
  // Use historical split when on the 30-day tab, daily split otherwise
  const medicalAidSplit = tab === 'history' ? historicalMaSplit : dailyMaSplit;
  const maPieLoading = tab === 'history' ? historicalMaLoading : summaryLoading;

  const hourlyData = Array.from({ length: 24 }, (_, h) => {
    // MySQL returns hour as string or number depending on driver — coerce both sides
    const found = hourlyVolume?.find(x => Number(x.hour) === h);
    return {
      hour: `${String(h).padStart(2, '0')}:00`,
      total: Number(found?.total) || 0,
      successful: Number(found?.successful) || 0,
      failed: (Number(found?.total) || 0) - (Number(found?.successful) || 0),
    };
  });

  // Distinct colours for up to 12 medical aids
  const MA_COLORS = [
    '#00d4ff','#00e5a0','#ffc23b','#ff4560','#7c5cbf',
    '#ff7043','#26c6da','#66bb6a','#ef5350','#ab47bc',
    '#ff7676','#29b6f6',
  ];

  const maData = (medicalAidSplit || []).map((d, i) => ({
    ...d,
    value: Number(d.value),
    color: MA_COLORS[i % MA_COLORS.length],
  }));

  const today = new Date().toLocaleDateString('en-ZA', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  const chartData = tab === 'hourly' ? hourlyData : (history || []);
  const chartLoading = tab === 'hourly' ? summaryLoading : historyLoading;

  return (
    <div className="animate-in">
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <div className="page-subtitle">{today}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* Datasource toggle — L4 only */}
          {user?.accesslevel >= 4 && datasource && (
            <button
              className={`btn btn-sm ${datasource === 'log' ? 'btn-success' : 'btn-warning'}`}
              onClick={handleDsToggle}
              disabled={dsToggling}
              title={`Currently reading from ${datasource === 'log' ? 'log files' : 'database'}. Click to switch.`}
              style={{ fontSize: 11, padding: '4px 10px', fontFamily: 'var(--font-mono)' }}
            >
              {dsToggling ? '…' : `⬤ ${datasource.toUpperCase()}`}
            </button>
          )}
          {/* Countdown ring + label */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: 8, padding: '6px 12px',
          }}>
            {/* Animated ring */}
            <svg width="24" height="24" style={{ flexShrink: 0 }}>
              <circle cx="12" cy="12" r="9" fill="none" stroke="var(--border-bright)" strokeWidth="2.5" />
              <circle cx="12" cy="12" r="9" fill="none"
                stroke="var(--accent)" strokeWidth="2.5"
                strokeDasharray={`${2 * Math.PI * 9}`}
                strokeDashoffset={`${2 * Math.PI * 9 * (1 - countdown / refreshInterval)}`}
                strokeLinecap="round"
                transform="rotate(-90 12 12)"
                style={{ transition: 'stroke-dashoffset 0.9s linear' }}
              />
            </svg>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--text-primary)', lineHeight: 1 }}>
                {formatCountdown(countdown)}
              </div>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 1 }}>next refresh</div>
            </div>
          </div>
          {/* Interval selector */}
          <select
            className="form-select"
            style={{ fontSize: 12, padding: '6px 10px', minWidth: 120 }}
            value={refreshInterval}
            onChange={e => handleIntervalChange(e.target.value)}
          >
            <option value={300}>Every 5 min</option>
            <option value={600}>Every 10 min</option>
            <option value={900}>Every 15 min</option>
          </select>
          <button className="btn btn-secondary btn-sm" onClick={handleManualRefresh}>↻ Refresh</button>
        </div>
      </div>

      {/* KPI Cards — skeleton per card while loading */}
      <div className="grid-4" style={{ marginBottom: 24 }}>
        {summaryLoading ? (
          [...Array(4)].map((_, i) => (
            <div key={i} className="card" style={{ position: 'relative', overflow: 'hidden' }}>
              <div className="skeleton" style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2 }} />
              <div className="skeleton" style={{ width: '60%', height: 11, marginBottom: 12 }} />
              <div className="skeleton" style={{ width: '40%', height: 32, marginBottom: 8 }} />
              <div className="skeleton" style={{ width: '70%', height: 11 }} />
            </div>
          ))
        ) : (
          <>
            <StatCard label="Transactions Today" value={summary?.txToday?.toLocaleString()}
              sub={`${summary?.successRate}% success rate`} color="var(--accent)" icon="⇄" />
            <StatCard label="Successful" value={summary?.txSuccessful?.toLocaleString()}
              color="var(--green)" icon="✓" />
            <StatCard label="Failed" value={summary?.txFailed?.toLocaleString()}
              color="var(--red)" icon="✕" />
            <StatCard label="Active Mailboxes" value={summary?.mailboxTotal}
              sub={summary?.mailboxBlocked > 0 ? `${summary.mailboxBlocked} blocked` : 'All active'}
              color="var(--yellow)" icon="⊟" />
          </>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 20, marginBottom: 20 }}>
        {/* Volume chart */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">Transaction Volume</span>
            <div style={{ display: 'flex', gap: 6 }}>
              {['hourly', 'history'].map(t => (
                <button key={t} className={`btn btn-sm ${tab === t ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => setTab(t)}>
                  {t === 'hourly' ? 'Today (hourly)' : '30-day history'}
                </button>
              ))}
            </div>
          </div>
          {chartLoading ? <ChartSkeleton height={220} /> : (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={chartData} margin={{ top: 5, right: 45, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="gradSuccess" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={CHART_COLORS.success} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={CHART_COLORS.success} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gradFailed" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={CHART_COLORS.failed} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={CHART_COLORS.failed} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey={tab === 'hourly' ? 'hour' : 'date'}
                  tick={{ fill: 'var(--text-muted)', fontSize: 10 }}
                  interval={tab === 'hourly' ? 3 : 4} />
                {/* Left axis — 0–20k in 2k increments for hourly, 0–200k in 50k for history */}
                <YAxis yAxisId="success" orientation="left"
                  tick={{ fill: CHART_COLORS.success, fontSize: 10 }}
                  {...(tab === 'hourly' ? {
                    domain: [0, 20000],
                    ticks: [0,2000,4000,6000,8000,10000,12000,14000,16000,18000,20000],
                    tickFormatter: v => `${(v/1000).toFixed(0)}k`,
                  } : {
                    domain: [0, 250000],
                    ticks: [0,50000,100000,150000,200000,250000],
                    tickFormatter: v => `${(v/1000).toFixed(0)}k`,
                  })} />
                {/* Right axis — failed transactions, independent scale */}
                <YAxis yAxisId="failed" orientation="right"
                  tick={{ fill: CHART_COLORS.failed, fontSize: 10 }}
                  tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                <Tooltip content={<CustomTooltip />} />
                <Area yAxisId="success" type="monotone" dataKey="successful"
                  stroke={CHART_COLORS.success} fill="url(#gradSuccess)"
                  strokeWidth={2} name="Successful" />
                <Area yAxisId="failed" type="monotone" dataKey="failed"
                  stroke={CHART_COLORS.failed} fill="url(#gradFailed)"
                  strokeWidth={2} name="Failed" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Medical Aid Split */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">
              Medical Aid Split — {tab === 'history' ? '30 days' : 'Today'}
            </span>
          </div>
          {maPieLoading ? <ChartSkeleton height={260} /> : (maData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={maData}
                  cx="50%"
                  cy="42%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {maData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(v, name) => [v.toLocaleString(), name]}
                  contentStyle={{
                    background: '#131a22',
                    border: '1px solid #2a3f57',
                    borderRadius: 6,
                    fontSize: 12,
                    color: '#e8f0fa',
                  }}
                  labelStyle={{ color: '#7a9bbf' }}
                  itemStyle={{ color: '#e8f0fa' }}
                />
                <Legend
                  layout="vertical"
                  align="right"
                  verticalAlign="middle"
                  iconType="circle"
                  iconSize={8}
                  formatter={(v) => (
                    <span style={{ color: 'var(--text-secondary)', fontSize: 11 }}>{v}</span>
                  )}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height: 260, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
              No transactions {tab === 'history' ? 'in the last 30 days' : 'today'}
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Top Mailboxes */}
        <div className="card">
          <div className="card-header"><span className="card-title">Top Mailboxes Today</span></div>
          {summaryLoading ? <ChartSkeleton height={280} /> : topMailboxes?.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={topMailboxes.slice(0, 8)} layout="vertical"
                margin={{ top: 0, right: 50, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
                <XAxis type="number"
                  domain={[0, 1000]}
                  ticks={[0, 200, 400, 600, 800, 1000]}
                  tick={{ fill: 'var(--text-muted)', fontSize: 10 }} />
                <YAxis type="category" dataKey="name"
                  tick={{ fill: 'var(--text-secondary)', fontSize: 10 }}
                  width={160} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="successful" fill={CHART_COLORS.success} name="Successful" radius={[0, 3, 3, 0]} />
                <Bar dataKey="txcount" fill={CHART_COLORS.muted} name="Total" radius={[0, 3, 3, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>No data</div>
          )}
        </div>

        {/* Recent Transactions */}
        <div className="card">
          <div className="card-header"><span className="card-title">Recent Transactions</span></div>
          <div className="table-container" style={{ maxHeight: 220, overflow: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr><th>Time</th><th>Mailbox</th><th>Type</th><th>Result</th></tr>
              </thead>
              <tbody>
                {summaryLoading ? <TableSkeleton rows={5} cols={4} /> : recentTransactions?.length > 0 ? (
                  recentTransactions.map(tx => (
                    <tr key={tx.txid}>
                      <td>{new Date(tx.txdatetime).toLocaleTimeString()}</td>
                      <td style={{ color: 'var(--text-primary)' }}>{tx.mailboxname || tx.mailboxid}</td>
                      <td>{tx.txtype}</td>
                      <td>
                        <span className={`badge ${tx.txresult === '0' || tx.txresult === 'OK' ? 'badge-success' : 'badge-error'}`}>
                          {tx.txresult === '0' ? 'OK' : tx.txresult || '—'}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={4} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>No transactions today</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

    </div>
  );
}
